/**
 * Persists viewer UI state across page reloads — AND keeps multiple open
 * browser tabs independent.
 *
 * Two storages, by design:
 *   - LIVE  → `sessionStorage`: scoped to ONE browser tab/window, survives a
 *     reload of that tab. So two tabs of the viewer never fight over one
 *     another's open tabs / selection / camera.
 *   - SEED  → `localStorage`: the last session's snapshot. A brand-new browser
 *     tab (empty sessionStorage) hydrates from it as a starting point, then
 *     diverges into its own sessionStorage.
 *
 * Survives: open universal-tabs + active tab, each tab's view (selection +
 * aspect + process scrubber), the camera (focal path + pan/zoom), and the
 * sidebar. Failures (quota, disabled, SSR, malformed JSON) are silently
 * swallowed — persistence is a UX nicety, never a correctness requirement.
 *
 * Versioning: a numeric `v` field invalidates stale shapes cleanly.
 */

import { hostStorage } from "./host/hostStorage.js";
import type { Tab } from "./workbench/tabs.js";
import type {
  TabViewEntry,
  WorkspaceSelection,
} from "./workbench/WorkspaceStore.js";
import { isClauseRow, type ViewConfig } from "./views/clauseModel.js";

const STORAGE_KEY = "arch:viewer-state";
// v4: viewport is centre-canonical (`center` instead of `pan`) — old pan-based
// entries are dropped on read (pre-release, no migration needed).
const VERSION = 4;

const sessionStore = (): Storage | null =>
  typeof sessionStorage !== "undefined" ? sessionStorage : null;
// The SEED leg goes through the host seam, not `localStorage` directly: a
// native webview gets a per-instance origin, so the last session's snapshot
// would be invisible to the next one. `hostStorage` is localStorage on the web
// and host-durable inside an IDE. (The session leg stays raw — it is per-tab
// by definition, and a webview reload is a new tab either way.)
const localStore = (): Pick<Storage, "getItem" | "setItem" | "removeItem"> =>
  hostStorage;

export interface PersistedViewport {
  readonly focalPath: readonly string[];
  /** Focal-space point under the screen centre — size-independent, so a restore
   *  at a different window size keeps the same centre (not the same pan). */
  readonly center: { readonly x: number; readonly y: number };
  readonly zoom: number;
}

export type PersistedSidebarTab = "details" | "process" | "diagnostics";

export interface PersistedViewerState {
  readonly viewport: PersistedViewport | null;
  readonly selectedModuleId: string | null;
  readonly selectedEdgeId: string | null;
  /** Qualified name of a directly-selected interface, or null. */
  readonly selectedInterfaceKey: string | null;
  readonly sidebarCollapsed: boolean | null;
  readonly sidebarTab: PersistedSidebarTab | null;
  /** The universal-tab snapshot — open tabs (board/file/doc/process/types) with
   *  their per-tab IDENTITY state (board camera/root, doc anchor) + the active
   *  tab. */
  readonly tabs?: readonly Tab[];
  readonly activeTabId?: string | null;
  /** Per-tab VIEW records (selection / aspect / process scrubber), keyed by tab
   *  id — restored so each tab reopens on exactly what it was looking at. */
  readonly views?: readonly TabViewEntry[];
}

/** The JSON-safe form of one per-tab view record (Sets flattened to arrays;
 *  transient/derived slices — navHistory, blastRadius — are not persisted). */
interface StoredTabView {
  readonly id: string;
  readonly selection: WorkspaceSelection;
  readonly colorKey: string | null;
  readonly webKeys: readonly string[];
  readonly replicatedKeys?: readonly string[];
  readonly stepIndex: number | null;
  readonly autoPlay: boolean;
  /** The view configurator's per-tab config (structured rows survive reload). */
  readonly builderConfig?: ViewConfig | null;
}

interface StoredPayload {
  readonly v: number;
  readonly viewport: PersistedViewport | null;
  readonly selectedModuleId: string | null;
  readonly selectedEdgeId: string | null;
  readonly selectedInterfaceKey?: string | null;
  readonly sidebarCollapsed?: boolean | null;
  readonly sidebarTab?: PersistedSidebarTab | null;
  readonly tabs?: readonly Tab[];
  readonly activeTabId?: string | null;
  readonly views?: readonly StoredTabView[];
}

/**
 * Read persisted state. LIVE state (this browser tab's sessionStorage) wins;
 * a fresh tab with none SEEDS from the last session (localStorage). Every
 * failure mode collapses to "no persisted state, fall back to defaults."
 */
export function loadPersistedViewerState(): PersistedViewerState | null {
  return readFrom(sessionStore()) ?? readFrom(localStore());
}

function readFrom(
  storage: Pick<Storage, "getItem"> | null,
): PersistedViewerState | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPayload;
    if (parsed?.v !== VERSION) return null;
    if (!isValidPayload(parsed)) return null;
    return {
      viewport: parsed.viewport,
      selectedModuleId: parsed.selectedModuleId,
      selectedEdgeId: parsed.selectedEdgeId,
      selectedInterfaceKey: parsed.selectedInterfaceKey ?? null,
      sidebarCollapsed: parsed.sidebarCollapsed ?? null,
      sidebarTab: parsed.sidebarTab ?? null,
      // Only surface the tab snapshot when one was stored — keeps the loaded
      // shape clean for callers (and round-trips) that never set tabs.
      ...(Array.isArray(parsed.tabs)
        ? {
            // Lenient per-entry validation, like `views`: a shape-corrupt
            // entry (truncated write) drops alone — an unchecked one reached
            // createWorkspaceStore and crashed construction on EVERY boot,
            // with the bad blob stuck in storage (r29).
            tabs: parsed.tabs.filter(isValidTabEntry),
            activeTabId: typeof parsed.activeTabId === "string" ? parsed.activeTabId : null,
          }
        : {}),
      ...(Array.isArray(parsed.views)
        ? { views: parsed.views.filter(isValidViewEntry).map(deserializeView) }
        : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Write persisted state to BOTH stores: sessionStorage (live, this browser
 * tab) + localStorage (seed, for the next brand-new tab). Caller debounces.
 */
export function savePersistedViewerState(state: PersistedViewerState): void {
  const payload: StoredPayload = {
    v: VERSION,
    viewport: state.viewport,
    selectedModuleId: state.selectedModuleId,
    selectedEdgeId: state.selectedEdgeId,
    selectedInterfaceKey: state.selectedInterfaceKey,
    sidebarCollapsed: state.sidebarCollapsed,
    sidebarTab: state.sidebarTab,
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    views: state.views?.map(serializeView),
  };
  const json = JSON.stringify(payload);
  try {
    sessionStore()?.setItem(STORAGE_KEY, json);
  } catch {
    // Quota exceeded, disabled, etc. — UX nicety, not correctness.
  }
  try {
    localStore()?.setItem(STORAGE_KEY, json);
  } catch {
    // ditto
  }
}

/** Drops the persisted state from BOTH stores. Used by "reset view". */
export function clearPersistedViewerState(): void {
  try {
    sessionStore()?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  try {
    localStore()?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Trivial debouncer for the save side. Returns a function that, when
 * called repeatedly within `ms` of the previous invocation, only
 * actually fires once after the burst quiets. Avoids hitting
 * storage on every wheel/drag frame.
 */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number
): (...args: A) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: A) => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn(...args);
    }, ms);
  };
}

// =============================================================================
// Helpers
// =============================================================================

function serializeView(e: TabViewEntry): StoredTabView {
  return {
    id: e.id,
    selection: e.view.selection,
    colorKey: e.view.aspect.colorKey,
    webKeys: [...e.view.aspect.webKeys],
    replicatedKeys: [...(e.view.aspect.replicatedKeys ?? [])],
    stepIndex: e.view.process.stepIndex,
    autoPlay: e.view.process.autoPlay,
    builderConfig: e.view.builderConfig,
  };
}

/** A persisted view entry the store can safely construct from — the `views`
 *  twin of `isValidTabEntry` (r29 validated `tabs`, one array short: an
 *  entry with `selection: null` nulled `state.selection` at boot and every
 *  selection consumer threw; a `webKeys: 5` threw INSIDE deserializeView and
 *  the outer catch discarded the ENTIRE persisted state, contradicting the
 *  documented "a malformed entry just loses that tab's view" posture). */
function isValidViewEntry(s: unknown): s is StoredTabView {
  if (typeof s !== "object" || s === null) return false;
  const v = s as { id?: unknown; selection?: unknown; webKeys?: unknown; replicatedKeys?: unknown };
  return (
    typeof v.id === "string" &&
    typeof v.selection === "object" &&
    v.selection !== null &&
    (v.webKeys == null || Array.isArray(v.webKeys)) &&
    (v.replicatedKeys == null || Array.isArray(v.replicatedKeys))
  );
}

/** Shape-validate a persisted `builderConfig` — the last field r30's entry
 *  validation left through verbatim. `useViewDrafts` dereferences
 *  `config.name.trim()` / `config.rows.map(...)` during BOARD render (the
 *  configurator mounts closed on every edit-capable board), so a truncated
 *  `{}` or wrong-typed `{rows: 5}` was a permanent boot crash-loop. Invalid →
 *  null (fresh scratch), keeping the entry's selection/aspect. */
function validBuilderConfig(c: unknown): ViewConfig | null {
  if (typeof c !== "object" || c === null) return null;
  const v = c as { rows?: unknown; name?: unknown; baseViewName?: unknown; dirty?: unknown };
  if (
    !Array.isArray(v.rows) ||
    typeof v.name !== "string" ||
    (v.baseViewName !== null && typeof v.baseViewName !== "string") ||
    typeof v.dirty !== "boolean"
  ) {
    return null;
  }
  // Rows validate STRUCTURALLY (`isClauseRow`, defined beside the row types):
  // an emitter probe was tried first and failed — `rowText` is lenient about
  // fields the row's current state doesn't emit (payload-less atom → "",
  // plain-mode flow never touches `body`) while the popover editors
  // dereference them strictly (bugloop r33). Malformed rows drop alone;
  // incomplete-but-well-shaped rows survive by design.
  const rows = v.rows.filter(isClauseRow);
  return rows.length === v.rows.length ? (c as ViewConfig) : { ...(c as ViewConfig), rows };
}

function deserializeView(s: StoredTabView): TabViewEntry {
  return {
    id: s.id,
    view: {
      selection: s.selection,
      // Transient/derived slices reset on restore — not persisted.
      navHistory: [],
      blastRadius: [],
      aspect: {
        colorKey: s.colorKey ?? null,
        legendHover: null,
        legendPinned: null,
        webKeys: new Set(s.webKeys ?? []),
        replicatedKeys: new Set(s.replicatedKeys ?? []),
      },
      process: { stepIndex: s.stepIndex ?? null, autoPlay: s.autoPlay ?? false },
      builderConfig: validBuilderConfig(s.builderConfig),
    },
  };
}

function isValidPayload(p: StoredPayload): boolean {
  if (typeof p !== "object" || p === null) return false;
  if (p.viewport !== null) {
    if (typeof p.viewport !== "object" || p.viewport === null) return false;
    if (!Array.isArray(p.viewport.focalPath)) return false;
    if (typeof p.viewport.zoom !== "number" || !Number.isFinite(p.viewport.zoom)) return false;
    if (typeof p.viewport.center?.x !== "number" || typeof p.viewport.center?.y !== "number") return false;
  }
  if (p.selectedModuleId !== null && typeof p.selectedModuleId !== "string") return false;
  if (p.selectedEdgeId !== null && typeof p.selectedEdgeId !== "string") return false;
  if (p.selectedInterfaceKey != null && typeof p.selectedInterfaceKey !== "string") return false;
  if (p.sidebarCollapsed != null && typeof p.sidebarCollapsed !== "boolean") return false;
  if (
    p.sidebarTab != null &&
    p.sidebarTab !== "details" &&
    p.sidebarTab !== "process" &&
    p.sidebarTab !== "diagnostics"
  ) {
    return false;
  }
  // `views`, when present, must be an array — entries are lenient (best-effort
  // restore; a malformed entry just loses that tab's view, not the whole load).
  if (p.views != null && !Array.isArray(p.views)) return false;
  return true;
}

/** A persisted tab entry the store can safely construct from: object shape
 *  with string id/kind and an object state. Semantic validity (kind arms,
 *  file existence, custom revive) is the store's reconcile/revive job —
 *  this only rejects shapes that would THROW before reaching it. */
function isValidTabEntry(t: unknown): boolean {
  if (typeof t !== "object" || t === null) return false;
  const tab = t as { id?: unknown; kind?: unknown; state?: unknown };
  return (
    typeof tab.id === "string" &&
    typeof tab.kind === "string" &&
    typeof tab.state === "object" &&
    tab.state !== null
  );
}
